import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { TeamMember } from '@/types'

import Image from 'next/image'
import { ArrowDownRight } from 'lucide-react'
import { pad } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HeroTitle } from '@/components/generals/hero-title'
import { Mark } from '@/components/generals/mark'
import { RollText } from '@/components/generals/roll-text'
import { SectionLabel } from '@/components/generals/section-label'
import ProcessSection from '@/components/sections/process-section'
import TeamSection from '@/components/sections/team-section'
import PrinciplesList from '@/components/sections/principles-list'
import FinalCta from '@/components/sections/final-cta'

import { REVALIDATE_SECONDS, getTeam } from '@/lib/content'

interface AboutPageProps {
    team: TeamMember[]
}

const POSITIONING = [
    {
        title: "What we believe",
        body: "Great marketing is both memorable and measurable. A beautiful campaign that sells nothing has failed, and so has an efficient one that nobody remembers."
    },
    {
        title: "Who we work with",
        body: "Ambitious businesses with a clear growth objective, from funded startups preparing a launch to established brands moving into new markets."
    },
    {
        title: "How we are different",
        body: "Strategists, creatives and performance specialists sit on the same team, so ideas are built to be measured and measurement feeds the next idea."
    }
]

const ABOUT_LINKS = [
    { label: "Our story", href: "#story" },
    { label: "How we work", href: "#process" },
    { label: "Meet the team", href: "#team" }
]

const PHILOSOPHY = [
    { title: "One idea at the center", body: "Every campaign is built around a single, sharp thought that every asset can carry." },
    { title: "Made for the channel", body: "Ideas are shaped for where people will actually see them, not adapted as an afterthought." },
    { title: "Tested before it scales", body: "We put early concepts in front of real audiences, then invest in the ones that connect." }
]

const AboutPage: FC<AboutPageProps> = ({ team }): ReactNode => {
    return (
        <Layout>
            <Seo title='About us' description='BLITZ is a creative marketing agency combining strategic thinking, bold ideas and performance-driven execution to help ambitious businesses grow.' />

            <section id='about-hero' aria-labelledby='about-hero-title' data-nav-theme='light' className='relative overflow-hidden bg-cream text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 pb-20 pt-32 md:px-10 md:pb-28 md:pt-40'>
                    <div className='relative flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-5'>
                        <span aria-hidden='true' className='reveal-rule absolute inset-x-0 bottom-0 h-px bg-charcoal/20' style={revealDelay(100)} />
                        <SectionLabel className='reveal-fade'>About BLITZ</SectionLabel>
                        <p className='reveal-fade font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal/75' style={revealDelay(150)}>
                            Strategy / Creative / Performance
                        </p>
                    </div>

                    <HeroTitle
                        id='about-hero-title'
                        className='mt-10 max-w-[18ch] type-hero md:mt-14 lg:max-w-none'
                        delay={200}
                        lines={[
                            'Built for brands that',
                            <>refuse to <Mark>blend in.</Mark></>
                        ]}
                    />

                    <div className='mt-12 grid gap-14 md:mt-16 lg:grid-cols-12 lg:gap-10'>
                        <figure className='relative pb-16 sm:pb-20 lg:col-span-7 lg:pb-24'>
                            <div aria-hidden='true' className='reveal-clip absolute -left-4 top-10 h-1/2 w-1/4 bg-coral md:-left-6' style={revealDelay(300)} />
                            <div className='reveal-clip relative aspect-[4/3] overflow-hidden bg-stone' style={revealDelay(360)}>
                                <Image src='/images/about/team-review.jpg' alt='Three people reviewing color and texture samples together' fill priority sizes='(min-width: 1024px) 55vw, 100vw' className='object-cover' />
                            </div>
                            <div className='reveal-clip absolute bottom-0 right-0 w-[46%] border-[6px] border-cream bg-cream md:border-8' style={revealDelay(620)}>
                                <div className='relative aspect-[4/3] overflow-hidden bg-stone'>
                                    <Image src='/images/about/small-egos.png' alt='Photographer shooting a model on a coral backdrop while the team reviews printed campaign proofs' fill sizes='(min-width: 1024px) 26vw, 46vw' className='object-cover object-[40%_center]' />
                                </div>
                            </div>
                            <figcaption className='reveal-fade absolute bottom-0 left-0 flex max-w-[48%] flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/80' style={revealDelay(800)}>
                                <span>01 / Material review</span>
                                <span>02 / Campaign shoot</span>
                            </figcaption>
                        </figure>

                        <div className='lg:col-span-4 lg:col-start-9 lg:self-end'>
                            <p className='reveal-fade text-[17px] leading-relaxed text-charcoal/85' style={revealDelay(420)}>
                                BLITZ is a creative marketing agency that combines strategic thinking, bold ideas, and performance-driven execution to help ambitious businesses grow.
                            </p>
                            <blockquote className='reveal-fade mt-10 border-t border-charcoal pt-6' style={revealDelay(520)}>
                                <p className='font-heading text-[clamp(1.4rem,2vw,1.85rem)] font-bold leading-[1.18] tracking-[-0.025em]'>
                                    Great marketing is both memorable and measurable.
                                </p>
                                <p className='mt-3 text-[15px] leading-relaxed text-charcoal/85'>
                                    Strategists, creatives and performance specialists working as one team on every account.
                                </p>
                            </blockquote>
                            <ul className='mt-8 border-t border-charcoal/25'>
                                {ABOUT_LINKS.map((link, index) => (
                                    <li key={link.href} className='reveal-fade border-b border-charcoal/25' style={revealDelay(620 + index * 70)}>
                                        <a
                                            href={link.href}
                                            className='group flex items-center justify-between gap-4 py-4 text-[13px] font-semibold uppercase tracking-[0.08em] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral'
                                        >
                                            <span className='flex items-baseline gap-4'>
                                                <span className='font-mono text-xs font-normal text-charcoal/75'>{pad(index + 1)}</span>
                                                <RollText>{link.label}</RollText>
                                            </span>
                                            <ArrowDownRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:translate-x-0.5 group-hover:translate-y-0.5' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section id='story' aria-labelledby='story-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
                <div className='mx-auto grid max-w-[1440px] gap-14 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                    <div data-reveal className='lg:col-span-5'>
                        <SectionLabel tone='dark'>Our story</SectionLabel>
                        <h2 id='story-title' className='mt-6 font-heading type-section'>
                            <span className='block'>Beautiful and effective.</span>
                            <span className='block text-coral'>Never one or the other.</span>
                        </h2>
                    </div>
                    <div className='space-y-6 text-[17px] leading-relaxed text-cream/80 lg:col-span-6 lg:col-start-7'>
                        <p data-reveal className='font-heading text-[clamp(1.3rem,2vw,1.75rem)] font-semibold leading-[1.28] tracking-[-0.02em] text-cream'>
                            BLITZ started with a simple frustration: too much marketing is either striking and ineffective, or measurable and forgettable.
                        </p>
                        <p data-reveal>
                            We built the agency to close that gap. Our strategists, creatives and performance specialists work as one team, so every idea is designed with a clear objective and every result informs the next piece of creative work.
                        </p>
                        <p data-reveal>
                            Today we help ambitious businesses define what they stand for, launch campaigns people notice, and grow through marketing they can measure. We keep our teams small and senior, our process transparent, and our attention on the numbers that matter to our clients.
                        </p>
                    </div>
                </div>
            </section>

            <section id='mission' aria-labelledby='mission-title' data-nav-theme='light' className='bg-stone text-charcoal'>
                <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                    <div data-reveal className='max-w-5xl'>
                        <SectionLabel>Mission and positioning</SectionLabel>
                        <h2 id='mission-title' className='mt-8 font-heading text-[clamp(1.7rem,3.2vw,3rem)] font-bold leading-[1.12] tracking-[-0.03em]'>
                            Our mission is to make ambitious businesses more visible, help their brands stand out, and turn marketing strategy into meaningful growth.
                        </h2>
                    </div>

                    <ul className='mt-14 grid border-t border-charcoal md:mt-20 md:grid-cols-3'>
                        {POSITIONING.map((item, index) => (
                            <li key={item.title} data-reveal className='border-b border-charcoal/30 py-8 md:border-b-0 md:border-l md:px-8 md:first:border-l-0 md:first:pl-0'>
                                <span className='font-mono text-xs text-charcoal/75'>{pad(index + 1)}</span>
                                <h3 className='mt-6 font-heading font-wide text-xl font-black uppercase tracking-[-0.015em] md:text-2xl'>{item.title}</h3>
                                <p className='mt-4 max-w-sm text-[15px] leading-relaxed text-charcoal/85'>{item.body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <ProcessSection
                label='Strategic approach'
                intro='Every engagement follows the same four moves, so strategy, creative and performance stay connected from the first workshop to the hundredth optimization.'
            />

            <section id='philosophy' aria-labelledby='philosophy-title' data-nav-theme='light' className='relative overflow-hidden bg-sand text-charcoal'>
                <div className='mx-auto grid max-w-[1440px] gap-14 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                    <div data-reveal className='lg:col-span-6'>
                        <SectionLabel>Creative philosophy</SectionLabel>
                        <h2 id='philosophy-title' className='mt-6 font-heading type-section'>
                            <span className='block'>Creativity is not decoration.</span>
                            <span className='block'>It is how brands get <Mark>noticed.</Mark></span>
                        </h2>
                    </div>
                    <ol className='border-t border-charcoal lg:col-span-5 lg:col-start-8 lg:self-end'>
                        {PHILOSOPHY.map((item, index) => (
                            <li key={item.title} data-reveal className='grid grid-cols-[3rem_1fr] gap-4 border-b border-charcoal/30 py-6'>
                                <span className='font-mono text-xs text-charcoal'>{pad(index + 1)}</span>
                                <div>
                                    <h3 className='font-heading font-wide text-lg font-black uppercase tracking-[-0.01em]'>{item.title}</h3>
                                    <p className='mt-2 text-[15px] leading-relaxed text-charcoal/85'>{item.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <TeamSection team={team} />

            <section id='principles' aria-labelledby='principles-title' data-nav-theme='light' className='bg-cream text-charcoal'>
                <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 md:py-28 lg:grid-cols-12 lg:gap-10'>
                    <div data-reveal className='lg:col-span-5'>
                        <SectionLabel>Agency principles</SectionLabel>
                        <h2 id='principles-title' className='mt-6 font-heading type-section'>
                            <span className='block'>Small egos.</span>
                            <span className='block'>Big ideas.</span>
                            <span className='block'><Mark>Clear results.</Mark></span>
                        </h2>
                    </div>
                    <PrinciplesList large className='lg:col-span-7' />
                </div>
            </section>

            <FinalCta />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutPageProps> = async () => {
    const team = await getTeam()

    return {
        props: { team },
        revalidate: REVALIDATE_SECONDS
    }
}

export default AboutPage
